import { useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type PaginationState,
  type VisibilityState,
  getSortedRowModel,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DataTablePagination } from '@/components/data-table'
import { type TransactionRecord } from '../types/transactions'
import { transactionsColumns as columns } from './transactions-columns'
import { useTransactionsStore } from '../stores/transactions-store'
import { useTransactionsActions } from '../hooks/useTransactionsActions'
import { cn } from '@/lib/utils'

interface TransactionsTableProps {
  data: TransactionRecord[]
  total: number
  loading?: boolean
}

export function TransactionsTable({ data, total, loading }: TransactionsTableProps) {
  const { page, limit } = useTransactionsStore((state) => state.pagination)
  const { sorting } = useTransactionsStore((state) => state.sorting)
  const { onPaginationChange, onSortingChange } = useTransactionsActions()
  
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: limit,
  }

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(total / limit),
    state: {
      pagination,
      columnVisibility,
      sorting,
    },
    manualPagination: true,
    onPaginationChange: (updater) => {
        if (typeof updater === 'function') {
            const newState = updater(pagination)
            onPaginationChange(newState)
        } else {
            onPaginationChange(updater)
        }
    },
    onSortingChange: (updater) => {
        if (typeof updater === 'function') {
            const newState = updater(sorting)
            onSortingChange(newState)
        } else {
            onSortingChange(updater)
        }
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className='flex flex-1 flex-col gap-4 min-h-0'>
      <div className='rounded-md border flex-1 overflow-y-auto overflow-x-auto bg-card relative'>
        <Table className='min-w-[600px] sm:min-w-0'>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
                 <TableRow>
                 <TableCell
                   colSpan={columns.length}
                   className='h-24 text-center'
                 >
                   加载中...
                 </TableCell>
               </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className={cn(cell.column.columnDef.meta?.className)}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className='h-24 text-center'
                >
                  暂无记录
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  )
}
